import { Controller } from '@hotwired/stimulus'
import { VoiceRecordingService } from './service.js'
import { MicrophoneManager } from '../shared/microphone_manager.js'
import { formatTime } from '../shared/format_time.js'
import { serverAcknowledgedDurableSave } from './interrupted_recording_helpers.js'
import './button_component.css'
import $ from 'jquery'

export default class extends Controller {
  static targets = [
    'recordingControls',
    'pauseButton',
    'resumeButton',
    'timer',
    'cancelButton',
    'micButton',
    'playbackElementTemplate',
  ]

  static values = {
    playbackContainerSelector: String,
    restoreKey: String,
  }

  connect() {
    this.form = this.element.closest('form')
    this.recordingService = new VoiceRecordingService({
      microphoneManager: MicrophoneManager,
      // Persist locally so a dropped connection or closed tab stays recoverable
      // from the Interrupted recordings modal. No in-form restore toast here: a
      // brand-new post has no stable key to anchor recovery on.
      restoreKey: this.restoreKeyValue || null,
    })

    // Once the post is durably saved, drop the local recovery copies. Best-effort:
    // fires only for remote (ajax) forms — otherwise retention purges them.
    this.form?.addEventListener('ajax:success', this.#commitRecordings)

    this.timerInterval = null
    this.isRecording = false
    this.isPaused = false

    this.#bindRecordingEvents()
  }

  disconnect() {
    this.#clearTimerInterval()
    this.form?.removeEventListener('ajax:success', this.#commitRecordings)
    if (this.recordingService) {
      this.recordingService.destroy()
    }
  }

  // Only the host form's OWN submit means the post was saved. UJS ajax events
  // bubble, so ignore ajax:success from nested remote actions. Soft 200/{alert}
  // responses are not durable saves — keep the IndexedDB recovery copy.
  #commitRecordings = (event) => {
    if (event.target !== this.form) return
    const data = event.detail?.[0]
    if (!serverAcknowledgedDurableSave(data)) return
    this.recordingService?.commitConsumed()
  }

  pause() {
    if (this.isRecording && !this.isPaused) {
      this.recordingService.pause()
    }
  }

  resume() {
    if (this.isRecording && this.isPaused) {
      this.recordingService.resume()
    }
  }

  cancel() {
    if (this.isRecording) {
      this.recordingService.cancel()
    }
  }

  toggleRecording() {
    if (!this.isRecording) {
      this.#startRecording()
    } else {
      this.#stopRecording()
    }
  }

  #bindRecordingEvents() {
    this.recordingService.on('started', () => {
      this.isRecording = true
      this.isPaused = false
      this.element.classList.add('voice-recording-button--recording')
      this.#recordingScope()?.classList.add('is-voice-recording')
      this.#startTimerUpdates()
      this.#dispatchToMicButton('mic-button:show-active')
    })

    this.recordingService.on('paused', () => {
      this.isPaused = true
      this.element.classList.add('voice-recording-button--paused')
    })

    this.recordingService.on('resumed', () => {
      this.isPaused = false
      this.element.classList.remove('voice-recording-button--paused')
    })

    this.recordingService.on('stopped', (data) => {
      this.#clearTimerInterval()
      this.#handleRecordingComplete(data.blob)
      this.#dispatchToMicButton('mic-button:show-inactive')
    })

    this.recordingService.on('destroyed', () => {
      this.#clearTimerInterval()
      this.#resetToInitialState()
      this.#dispatchToMicButton('mic-button:show-inactive')
    })

    // Without this, a denied microphone permission leaves the mic button spinning
    // forever and never tells the user why.
    this.recordingService.on('error', (data) => {
      this.#clearTimerInterval()
      this.#resetToInitialState()
      this.#dispatchToMicButton('mic-button:show-inactive')
      window.showNotice?.(
        'alert',
        data?.error?.message || 'We couldn’t start recording.'
      )
    })
  }

  #startRecording() {
    this.#dispatchToMicButton('mic-button:show-loading')
    this.recordingService.start()
  }

  #stopRecording() {
    this.recordingService.stop()
  }

  #handleRecordingComplete(blob) {
    this.#resetToInitialState()
    const playbackElement = this.#createPlaybackElement(blob)

    // If a playback container is specified, append the element there
    if (this.hasPlaybackContainerSelectorValue) {
      const container = document.querySelector(
        this.playbackContainerSelectorValue
      )
      if (container) {
        container.appendChild(playbackElement)
        $(playbackElement)?.changed?.()
      }
    }

    // Always dispatch the event for additional handling
    this.dispatch('recording-completed', {
      detail: { element: playbackElement },
    })
  }

  #resetToInitialState() {
    this.isRecording = false
    this.isPaused = false
    this.element.classList.remove(
      'voice-recording-button--recording',
      'voice-recording-button--paused'
    )
    this.#recordingScope()?.classList.remove('is-voice-recording')
  }

  // The host composer restyles itself while recording off a plain is-voice-recording class on
  // this element: the nearest .js-voice-recording-scope, else the form. Not a
  // :has(.voice-recording-button--recording) in the host's CSS: those made every DOM insert on
  // the page restyle the whole document.
  #recordingScope() {
    return this.element.closest('.js-voice-recording-scope') || this.form
  }

  #createPlaybackElement(blob) {
    const audioUrl = URL.createObjectURL(blob)

    // Clone template and populate with data
    const playbackElement = this.playbackElementTemplateTarget.content
      .cloneNode(true)
      .querySelector('div')
    const audioEl = playbackElement.querySelector('audio')
    const audioSource = playbackElement.querySelector('source')
    audioSource.src = audioUrl
    audioSource.type = blob.type
    audioEl.dataset.src = audioUrl
    audioEl.dataset.contentType = blob.type

    // Set up file input
    const fileInput = playbackElement.querySelector('.js-recording-file-input')
    this.recordingService.setFileWithFixedDurationOnInput(
      fileInput,
      this.#generateVoiceRecordingFilename()
    )

    return playbackElement
  }

  deleteRecording() {
    // This method can be called on playback elements if needed
    // Implementation depends on how the parent handles the playback element
  }

  #startTimerUpdates() {
    this.timerInterval = setInterval(() => {
      this.#updateTimerDisplay()
    }, 500)
    this.#updateTimerDisplay() // Update immediately
  }

  #clearTimerInterval() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  #updateTimerDisplay() {
    const duration = this.recordingService.getDuration()
    this.timerTarget.textContent = formatTime(duration)
  }

  #dispatchToMicButton(eventName, detail = {}) {
    this.dispatch(eventName, {
      detail,
      target: this.micButtonTarget,
      prefix: false,
    })
  }

  #generateVoiceRecordingFilename() {
    const now = new Date()

    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')

    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')

    return `VOICE_RECORDING_${year}${month}${day}_${hours}_${minutes}`
  }
}
